/**
 * THE SHARED RESEARCH INSTRUMENTS ON A SPACE PAGE — trials, method candidates,
 * standing programs, the Owner's provider grant and the portable dossier.
 *
 * WHAT THIS SURFACE REFUSES TO DO. It does not create a trial design on the
 * Owner's behalf: a trial is the researcher's pre-registered comparison, and a
 * form here would turn the Owner into the author of the hypothesis. The Owner
 * reads the design, the readings, the verdict and the limitations; the
 * researcher (or the HTTP/CLI surface) authors them. The Owner's own acts on
 * this panel are the consequences: validating a candidate against its evidence,
 * authorizing an adoption, stopping a program, granting a provider, exporting
 * the dossier.
 */

import { useEffect, useState } from 'preact/hooks';
import {
	adoptMethodCandidate,
	createResearchProgram,
	listCandidateReviews,
	reviewCandidateNovelty,
	listCapabilityAdoptions,
	listMethodCandidates,
	listModelProviderGrants,
	listResearchPrograms,
	listResearchTrials,
	probeProviderReachability,
	readResearchTrial,
	saveModelProviderGrant,
	setResearchProgramStatus,
	verifyCapabilityAdoption,
	spaceDossierUrl,
	startResearchProgram,
	validateMethodCandidate,
	type CandidateNoveltyReview,
	type CapabilityAdoption,
	type MethodCandidate,
	type ModelCapabilityGap,
	type ProviderGrant,
	type ProviderReachability,
	type ProviderStatus,
	type ResearchProgram,
	type ResearchTrialDetail,
	type ResearchTrialSummary,
	type SpaceSchedule,
} from '../space';

function reasonOf(error: { message: string } | null | undefined): string {
	return error?.message ?? 'the request failed without a reason';
}

function PanelNotice({ message }: { message: string | null }) {
	if (message === null) return null;
	return (
		<p class="panel small" role="status" style="padding:8px 12px;">
			{message}
		</p>
	);
}

export function SpaceInstrumentsPanel(props: {
	spaceId: string;
	spaceName: string;
	spaceSchedule: SpaceSchedule | null;
}) {
	const [trials, setTrials] = useState<ResearchTrialSummary[]>([]);
	const [trialDetail, setTrialDetail] = useState<ResearchTrialDetail | null>(null);
	const [candidates, setCandidates] = useState<MethodCandidate[]>([]);
	const [reviews, setReviews] = useState<Record<string, CandidateNoveltyReview[]>>({});
	const [noveltyJudge, setNoveltyJudge] = useState<
		Record<string, { provider: string; model: string }>
	>({});
	const [adoptions, setAdoptions] = useState<CapabilityAdoption[]>([]);
	const [programs, setPrograms] = useState<ResearchProgram[]>([]);
	const [grants, setGrants] = useState<ProviderGrant[]>([]);
	const [providerStatuses, setProviderStatuses] = useState<ProviderStatus[]>([]);
	const [capabilityGaps, setCapabilityGaps] = useState<ModelCapabilityGap[]>([]);
	const [reachability, setReachability] = useState<Record<string, ProviderReachability>>({});
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [verifyTrialId, setVerifyTrialId] = useState<Record<string, string>>({});
	const [adoptTarget, setAdoptTarget] = useState<Record<string, string>>({});
	const [adoptNote, setAdoptNote] = useState<Record<string, string>>({});
	const [grantProvider, setGrantProvider] = useState<'deepseek' | 'gemini'>('deepseek');
	const [grantModel, setGrantModel] = useState('deepseek-flash');
	const [grantMax, setGrantMax] = useState('0.25');
	const [programName, setProgramName] = useState('');
	const [programObjective, setProgramObjective] = useState('');
	const [programTotal, setProgramTotal] = useState('5');
	const [programPerRun, setProgramPerRun] = useState('1');
	const [programPerDay, setProgramPerDay] = useState('2');
	const [programStarts, setProgramStarts] = useState('');
	const [programEnds, setProgramEnds] = useState('');
	const [programRuns, setProgramRuns] = useState('5');
	const [programUseSchedule, setProgramUseSchedule] = useState(false);

	async function load(): Promise<void> {
		const [trialResult, candidateResult, adoptionResult, programResult, grantResult] =
			await Promise.all([
				listResearchTrials(props.spaceId),
				listMethodCandidates(props.spaceId),
				listCapabilityAdoptions(),
				listResearchPrograms(props.spaceId),
				listModelProviderGrants(),
			]);
		if (trialResult.ok) setTrials(trialResult.data.trials ?? []);
		else setNotice(reasonOf(trialResult.error));
		if (candidateResult.ok) {
			const list = candidateResult.data.candidates ?? [];
			setCandidates(list);
			const entries = await Promise.all(
				list.map(async (candidate) => {
					const result = await listCandidateReviews(candidate.candidateId);
					return [
						candidate.candidateId,
						result.ok ? (result.data.reviews ?? []) : [],
					] as const;
				}),
			);
			setReviews(Object.fromEntries(entries));
		} else setNotice(reasonOf(candidateResult.error));
		if (adoptionResult.ok) setAdoptions(adoptionResult.data.adoptions ?? []);
		else setNotice(reasonOf(adoptionResult.error));
		if (programResult.ok) setPrograms(programResult.data.programs ?? []);
		else setNotice(reasonOf(programResult.error));
		if (grantResult.ok) {
			setGrants(grantResult.data.grants ?? []);
			setProviderStatuses(grantResult.data.providers ?? []);
			setCapabilityGaps(grantResult.data.gaps ?? []);
		} else setNotice(reasonOf(grantResult.error));
	}

	useEffect(() => {
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.spaceId]);

	async function openTrial(trialId: string): Promise<void> {
		setBusy(true);
		const result = await readResearchTrial(trialId);
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setTrialDetail(result.data.trial ?? null);
	}

	async function validate(candidateId: string): Promise<void> {
		setBusy(true);
		const result = await validateMethodCandidate(candidateId);
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(`${candidateId} is ${result.data.candidate?.status ?? 'updated'}.`);
		await load();
	}

	async function adopt(candidateId: string): Promise<void> {
		const target = (adoptTarget[candidateId] ?? '').trim();
		const note = (adoptNote[candidateId] ?? '').trim();
		if (target.length === 0 || note.length === 0) {
			setNotice('An adoption needs a target and a note. Adoption is not a status change.');
			return;
		}
		setBusy(true);
		const result = await adoptMethodCandidate(candidateId, { target, note });
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(
			`${candidateId} was adopted into “${target}” as ${result.data.adoption?.status ?? 'ADOPTED_UNVERIFIED'}. It is not yet VERIFIED; a later trial must measure it.`,
		);
		await load();
	}

	async function verifyAdoption(candidateId: string, adoptionId: string): Promise<void> {
		const trialId = (verifyTrialId[candidateId] ?? '').trim();
		if (trialId.length === 0) {
			setNotice('A verification needs the id of a later, evaluated trial.');
			return;
		}
		setBusy(true);
		const result = await verifyCapabilityAdoption(adoptionId, trialId);
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(
			`Adoption verification: ${result.data.adoption?.status ?? 'recorded'}. ${result.data.adoption?.verificationTrialId ?? ''}`,
		);
		await load();
	}

	async function reviewNovelty(candidateId: string): Promise<void> {
		const judge = noveltyJudge[candidateId] ?? {
			provider: 'gemini',
			model: 'gemini-3.8-flash',
		};
		setBusy(true);
		const result = await reviewCandidateNovelty(candidateId, {
			judgeProvider: judge.provider,
			judgeModel: judge.model,
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(
			`Novelty review ${result.data.review?.status ?? 'recorded'}: ${result.data.review?.verdict ?? 'no verdict'}. ${result.data.review?.rationale ?? ''}`,
		);
		await load();
	}

	async function saveGrant(): Promise<void> {
		const maxUsdPerCall = Number(grantMax);
		if (!Number.isFinite(maxUsdPerCall) || maxUsdPerCall <= 0) {
			setNotice('The per-call envelope must be a positive USD figure.');
			return;
		}
		setBusy(true);
		const result = await saveModelProviderGrant({
			provider: grantProvider,
			maxUsdPerCall,
			...(grantModel.trim().length === 0 ? {} : { allowedModels: [grantModel.trim()] }),
			note: 'Owner grant from the Research Space surface',
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(
			`${grantProvider} is AUTHORIZED at up to $${String(maxUsdPerCall)} per call. A credential must still exist in the deployment; an authorization is not a key.`,
		);
		await load();
	}

	/**
	 * ASK THE PROVIDER, NOT THE CONFIGURATION. Presence is what the status row
	 * already knows; this button makes one free model-list request and shows
	 * what came back. It is not an authorization and does not enable a paid
	 * call; it is the difference between "a key is set" and "the key works".
	 */
	async function checkReachability(provider: string): Promise<void> {
		setBusy(true);
		const result = await probeProviderReachability(provider);
		setBusy(false);
		if (!result.ok) {
			setNotice(`Reachability check for ${provider} failed: ${reasonOf(result.error)}`);
			return;
		}
		const answer = result.data.reachability;
		if (answer === undefined) {
			setNotice(`Reachability check for ${provider} returned no answer.`);
			return;
		}
		setReachability((current) => ({ ...current, [provider]: answer }));
		setNotice(
			`${provider}: ${answer.outcome}${answer.httpStatus === null ? '' : ` (HTTP ${answer.httpStatus})`}. ${answer.detail}`,
		);
	}

	async function createProgram(): Promise<void> {
		const total = Number(programTotal);
		const perRun = Number(programPerRun);
		const perDay = programPerDay.trim().length === 0 ? undefined : Number(programPerDay);
		const maxRuns = Number(programRuns);
		if (
			programName.trim().length === 0 ||
			programObjective.trim().length === 0 ||
			!Number.isFinite(total) ||
			!Number.isFinite(perRun) ||
			!Number.isFinite(maxRuns)
		) {
			setNotice('A program needs a name, an objective, positive budgets and a run limit.');
			return;
		}
		setBusy(true);
		const result = await createResearchProgram({
			name: programName.trim(),
			objective: programObjective.trim(),
			budgetTotalUsd: total,
			budgetPerRunUsd: perRun,
			...(perDay === undefined || !Number.isFinite(perDay) ? {} : { budgetPerDayUsd: perDay }),
			startsAt: programStarts.trim().length > 0 ? programStarts : new Date().toISOString(),
			endsAt:
				programEnds.trim().length > 0
					? programEnds
					: new Date(Date.now() + 7 * 24 * 60 * 60 * 1_000).toISOString(),
			maxRuns,
			spaceId: props.spaceId,
			...(programUseSchedule && props.spaceSchedule !== null
				? { schedule: props.spaceSchedule }
				: {}),
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice('The program was authorized. It admits runs only inside its envelope.');
		setProgramName('');
		setProgramObjective('');
		await load();
	}

	async function programAction(
		programId: string,
		action: 'pause' | 'resume' | 'stop' | 'start',
	): Promise<void> {
		setBusy(true);
		const result =
			action === 'start'
				? await startResearchProgram(programId, {})
				: await setResearchProgramStatus(programId, action);
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(`${programId}: ${action} accepted.`);
		await load();
	}

	const grantFor = (provider: string): ProviderGrant | undefined =>
		grants.find((grant) => grant.provider === provider);

	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Trials, methods and programs</h2>
			<p class="small muted">
				A trial is a pre-registered baseline/candidate comparison on DEV and HOLDOUT cases,
				scored objectively or by a different model. A finding is not a validated method, and
				a validated method is not an adopted capability.
			</p>
			<PanelNotice message={notice} />

			<h3>Research trials</h3>
			{trials.length === 0 ? (
				<p class="muted small">
					No trial has been registered for this space. The researcher can author one inside an
					authorized window; the Owner can submit a design over the HTTP surface.
				</p>
			) : (
				<ul class="list">
					{trials.map((trial) => (
						<li key={trial.trialId} class="panel" style="padding:10px; margin:8px 0;">
							<div>
								<code class="small">{trial.trialId}</code> <strong>{trial.verdict ?? trial.status}</strong>
							</div>
							<div class="small">{trial.question}</div>
							<div class="small muted">
								Prediction: {trial.prediction} · {trial.verdictReason ?? 'awaiting evaluation'}
							</div>
							<button
								type="button"
								class="button ghost"
								disabled={busy}
								onClick={() => void openTrial(trial.trialId)}
							>
								Read the readings
							</button>
						</li>
					))}
				</ul>
			)}
			{trialDetail !== null && (
				<div class="panel" style="padding:10px; margin:10px 0;">
					<div class="small">
						<strong>{trialDetail.trial.trialId}</strong> — {trialDetail.trial.verdict ?? trialDetail.trial.status}
					</div>
					<p class="small">{trialDetail.trial.evaluation?.reason ?? 'No evaluation yet.'}</p>
					<ul class="small">
						{(trialDetail.trial.evaluation?.dimensions ?? []).map((dimension) => (
							<li key={dimension.split}>
								{dimension.split}: candidate {dimension.candidate.mean?.toFixed(3) ?? 'no reading'} vs
								baseline {dimension.baseline.mean?.toFixed(3) ?? 'no reading'}; delta{' '}
								{dimension.delta?.toFixed(3) ?? 'unmeasured'}; candidate scored{' '}
								{dimension.candidate.scored}, non-readings {dimension.candidate.nonReadings}
							</li>
						))}
					</ul>
					<p class="small muted">{trialDetail.trial.evaluation?.limitations ?? ''}</p>
					<p class="small muted">
						{trialDetail.observations.length} durable reading(s). A non-reading is not a zero.
					</p>
					<a class="button ghost" href={`/api/scout/gateway/research/trials/${encodeURIComponent(trialDetail.trial.trialId)}/dossier?format=markdown`}>
						Download trial dossier
					</a>
				</div>
			)}

			<h3>Method candidates</h3>
			{candidates.length === 0 ? (
				<p class="muted small">No method candidate is packaged for this space.</p>
			) : (
				<ul class="list">
					{candidates.map((candidate) => {
						const adoption = adoptions.find(
							(entry) => entry.candidateId === candidate.candidateId,
						);
						return (
							<li key={candidate.candidateId} class="panel" style="padding:10px; margin:8px 0;">
								<div>
									<strong>{candidate.name}</strong> v{candidate.version}{' '}
									<code class="small">{candidate.status}</code>
								</div>
								<div class="small muted">Evidence: {candidate.evidenceRefs.join(', ')}</div>
								<div class="small">Applies to: {candidate.applicability}</div>
								<div class="small muted">Limits: {candidate.limitations}</div>
								{candidate.status === 'CANDIDATE' && (
									<button type="button" class="button ghost" disabled={busy} onClick={() => void validate(candidate.candidateId)}>
										Validate against its holdout evidence
									</button>
								)}
								{candidate.status === 'VALIDATED' && (
									<div style="margin-top:8px;">
										<input
											type="text"
											placeholder="Adoption target (where it is integrated)"
											value={adoptTarget[candidate.candidateId] ?? ''}
											onInput={(event) =>
												setAdoptTarget({
													...adoptTarget,
													[candidate.candidateId]: (event.target as HTMLInputElement).value,
												})
											}
										/>
										<input
											type="text"
											placeholder="Adoption note (what changed)"
											value={adoptNote[candidate.candidateId] ?? ''}
											onInput={(event) =>
												setAdoptNote({
													...adoptNote,
													[candidate.candidateId]: (event.target as HTMLInputElement).value,
												})
											}
										/>
										<button type="button" class="button ghost" disabled={busy} onClick={() => void adopt(candidate.candidateId)}>
											Record adoption (unverified)
										</button>
									</div>
								)}
								{adoption !== undefined && (
									<div class="small muted">
										Adoption {adoption.status}; verification trial:{' '}
										{adoption.verificationTrialId ?? 'not yet verified'}
									</div>
								)}
								{adoption?.status === 'ADOPTED_UNVERIFIED' && (
									<div style="margin-top:8px;">
										<input
											type="text"
											placeholder="Evaluated verification trial id"
											value={verifyTrialId[candidate.candidateId] ?? ''}
											onInput={(event) =>
												setVerifyTrialId({
													...verifyTrialId,
													[candidate.candidateId]: (event.target as HTMLInputElement).value,
												})
											}
										/>
										<button
											type="button"
											class="button ghost"
											disabled={busy}
											onClick={() => void verifyAdoption(candidate.candidateId, adoption.adoptionId)}
										>
											Verify adoption with that trial
										</button>
									</div>
								)}
								<div style="margin-top:8px;">
									<div class="small muted">
										Independent novelty review (the judge must be a different model):
									</div>
									<input
										type="text"
										placeholder="Judge provider"
										value={
											(noveltyJudge[candidate.candidateId] ?? { provider: 'gemini' })
												.provider
										}
										onInput={(event) =>
											setNoveltyJudge({
												...noveltyJudge,
												[candidate.candidateId]: {
													provider: (event.target as HTMLInputElement).value,
													model:
														noveltyJudge[candidate.candidateId]?.model ??
														'gemini-3.8-flash',
												},
											})
										}
									/>
									<input
										type="text"
										placeholder="Judge model"
										value={
											(noveltyJudge[candidate.candidateId] ?? {
												model: 'gemini-3.8-flash',
											}).model
										}
										onInput={(event) =>
											setNoveltyJudge({
												...noveltyJudge,
												[candidate.candidateId]: {
													provider:
														noveltyJudge[candidate.candidateId]?.provider ?? 'gemini',
													model: (event.target as HTMLInputElement).value,
												},
											})
										}
									/>
									<button
										type="button"
										class="button ghost"
										disabled={busy}
										onClick={() => void reviewNovelty(candidate.candidateId)}
									>
										Review novelty against prior art
									</button>
									{(reviews[candidate.candidateId] ?? []).slice(0, 1).map((review) => (
										<div class="small" key={review.reviewId}>
											<code>{review.status}</code> {review.verdict ?? 'no verdict'} via{' '}
											{review.searchProvider ?? 'no search provider'}: {review.rationale}
										</div>
									))}
								</div>
							</li>
						);
					})}
				</ul>
			)}

			<h3>Standing research programs</h3>
			{programs.length === 0 ? (
				<p class="muted small">No standing program is authorized for this space.</p>
			) : (
				<ul class="list">
					{programs.map((program) => (
						<li key={program.programId} class="panel" style="padding:10px; margin:8px 0;">
							<div>
								<strong>{program.name}</strong> <code class="small">{program.status}</code>
							</div>
							<div class="small">{program.objective}</div>
							<div class="small muted">
								total ${(program.budgetTotalMicrousd / 1_000_000).toFixed(2)}; per run $
								{(program.budgetPerRunMicrousd / 1_000_000).toFixed(2)}; per day{' '}
								{program.budgetPerDayMicrousd === null
									? 'none'
									: `$${(program.budgetPerDayMicrousd / 1_000_000).toFixed(2)}`}
								; runs {program.maxRuns}; {program.startsAt} to {program.endsAt}
							</div>
							{program.stopReason !== null && (
								<div class="small muted">Stop reason: {program.stopReason}</div>
							)}
							<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">
								{program.status === 'ACTIVE' && (
									<>
										<button type="button" class="button ghost" disabled={busy} onClick={() => void programAction(program.programId, 'start')}>
											Start a run now
										</button>
										<button type="button" class="button ghost" disabled={busy} onClick={() => void programAction(program.programId, 'pause')}>
											Pause
										</button>
									</>
								)}
								{program.status === 'PAUSED' && (
									<button type="button" class="button ghost" disabled={busy} onClick={() => void programAction(program.programId, 'resume')}>
										Resume
									</button>
								)}
								{(program.status === 'ACTIVE' || program.status === 'PAUSED') && (
									<button type="button" class="button ghost" disabled={busy} onClick={() => void programAction(program.programId, 'stop')}>
										Stop
									</button>
								)}
								<a class="button ghost" href={`/api/scout/gateway/research/programs/${encodeURIComponent(program.programId)}/dossier?format=markdown`}>
									Download dossier
								</a>
							</div>
						</li>
					))}
				</ul>
			)}
			<details style="margin-top:10px;">
				<summary class="small">Authorize a standing program</summary>
				<div style="padding:10px 0;">
					<input type="text" placeholder="Program name" value={programName} onInput={(event) => setProgramName((event.target as HTMLInputElement).value)} />
					<textarea placeholder="Objective: what the program is for" rows={3} value={programObjective} onInput={(event) => setProgramObjective((event.target as HTMLTextAreaElement).value)} style="display:block; width:100%;" />
					<div class="small muted">Budgets in USD. The run ceiling is the smallest of these, what remains in total and what remains today.</div>
					<input type="number" min="0.01" step="0.01" placeholder="Total USD" value={programTotal} onInput={(event) => setProgramTotal((event.target as HTMLInputElement).value)} />
					<input type="number" min="0.01" step="0.01" placeholder="Per run USD" value={programPerRun} onInput={(event) => setProgramPerRun((event.target as HTMLInputElement).value)} />
					<input type="number" min="0.01" step="0.01" placeholder="Per day USD (optional)" value={programPerDay} onInput={(event) => setProgramPerDay((event.target as HTMLInputElement).value)} />
					<input type="number" min="1" step="1" placeholder="Max runs" value={programRuns} onInput={(event) => setProgramRuns((event.target as HTMLInputElement).value)} />
					<div class="small muted">Start and end are ISO instants; blank means now and seven days.</div>
					<input type="text" placeholder="Starts at (ISO)" value={programStarts} onInput={(event) => setProgramStarts((event.target as HTMLInputElement).value)} />
					<input type="text" placeholder="Ends at (ISO)" value={programEnds} onInput={(event) => setProgramEnds((event.target as HTMLInputElement).value)} />
					<label class="small" style="display:block; margin:8px 0;">
						<input type="checkbox" checked={programUseSchedule} disabled={props.spaceSchedule === null} onChange={(event) => setProgramUseSchedule((event.target as HTMLInputElement).checked)} />{' '}
						Use this space's schedule to open runs automatically
					</label>
					<button type="button" class="button" disabled={busy} onClick={() => void createProgram()}>
						Authorize program
					</button>
				</div>
			</details>

			<h3>Experimental model authorization</h3>
			<p class="small muted">
				An Owner grant names what may be called and the most one call may cost. It applies to
				Main Scout and to spaces that do not narrow it. It is not a credential: the deployment
				must still hold the provider key.
			</p>
			<div class="small">
				{providerStatuses.length === 0
					? 'Provider status is not wired in this deployment.'
					: providerStatuses.map((status) => {
							const checked = reachability[status.provider];
							return (
								<div key={status.provider} style="margin:8px 0;">
									<span style="display:block; margin:4px 0;">
										<strong>{status.provider}</strong>: {status.grantStatus}; credential{' '}
										<code>{status.credentialName}</code>{' '}
										{status.credentialConfigured ? 'is configured' : 'is NOT configured'}.
										{status.models.length === 0
											? ' No reachable model is registered.'
											: ` Models: ${status.models.map((model) => model.model).join(', ')}.`}
									</span>
									<button
										type="button"
										class="button ghost small"
										disabled={busy}
										onClick={() => void checkReachability(status.provider)}
									>
										Ask the provider whether this credential works
									</button>
									{checked !== undefined && (
										<p class="small" style="margin:4px 0;">
											<strong>{checked.outcome}</strong>
											{checked.httpStatus === null ? '' : ` (HTTP ${checked.httpStatus})`} —{' '}
											{checked.detail}{' '}
											<span class="muted">
												It proves: {checked.proves}. Spend: {checked.spend}.
											</span>
											{' '}
											No credential value is read by this page.
											{checked.models.length > 0 && (
												<>
													{' '}
													Models reported:{' '}
													{checked.models.slice(0, 20).join(', ')}
													{checked.models.length > 20 ? ' …' : ''}.
												</>
											)}
										</p>
									)}
								</div>
							);
						})}
			</div>
			{capabilityGaps.length > 0 && (
				<details style="margin:8px 0;">
					<summary class="small">Named capability gaps (no model or transport)</summary>
					<ul class="small">
						{capabilityGaps.map((gap) => (
							<li key={gap.capability}>
								<code>{gap.capability}</code>: {gap.reason}
							</li>
						))}
					</ul>
				</details>
			)}
			<div style="display:flex; gap:8px; flex-wrap:wrap; margin:8px 0;">
				<select value={grantProvider} onChange={(event) => {
					const provider = (event.target as HTMLSelectElement).value === 'gemini' ? 'gemini' : 'deepseek';
					setGrantProvider(provider);
					setGrantModel(provider === 'deepseek' ? 'deepseek-flash' : 'gemini-3.8-flash');
				}}>
					<option value="deepseek">deepseek</option>
					<option value="gemini">gemini</option>
				</select>
				<input type="text" placeholder="Model id (optional allowlist)" value={grantModel} onInput={(event) => setGrantModel((event.target as HTMLInputElement).value)} />
				<input type="number" min="0.01" step="0.01" placeholder="Max USD per call" value={grantMax} onInput={(event) => setGrantMax((event.target as HTMLInputElement).value)} />
				<button type="button" class="button ghost" disabled={busy} onClick={() => void saveGrant()}>
					Authorize provider
				</button>
			</div>

			<h3>Portable dossier</h3>
			<p class="small muted">
				A self-contained export of this space's documents, objectives, trials, readings,
				candidates, adoptions, programs, investigations and costs, with a SHA-256 per section
				and for the dossier. The JSON is the record; the Markdown is a projection.
			</p>
			<div style="display:flex; gap:8px; flex-wrap:wrap;">
				<a class="button" href={spaceDossierUrl(props.spaceId, 'markdown')}>Download dossier (Markdown)</a>
				<a class="button ghost" href={spaceDossierUrl(props.spaceId, 'json')}>Download dossier (JSON)</a>
			</div>
		</div>
	);
}
